import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage as FM } from 'react-intl';
import { required } from 'redux-form-validators';
import { Field, reduxForm, formValueSelector, change } from 'redux-form';

import { DefaultButton, ReduxSelectField, ReduxTextField, TextAreaField } from 'common/components';
import { stripRtlWordMarks, reverseWordsKeepWhitespaceByLines } from 'common/utils';

import staticStyles from './style';

import { editTemplate } from '../../_redux';

const EditTemplateForm = ({
  id,
  languages,
  language,
  submitting,
  invalid,
  pristine,
  handleSubmit,
  dispatch,
  history,
  error,
}) => {
  const isArabic = language === 'ar';

  // ar 模板：表单里按逻辑序（输入顺序）编辑，提交时转成阿语展示序入库，后端可直接取出使用；
  // 其他语言原样入库。均先剥离历史数据里可能残留的方向标记。
  const submit = handleSubmit(({ title, text, ...rest }) => {
    const cleanTitle = stripRtlWordMarks(title);
    const cleanText = stripRtlWordMarks(text);
    return editTemplate(
      {
        id,
        ...rest,
        title: isArabic ? reverseWordsKeepWhitespaceByLines(cleanTitle) : cleanTitle,
        text: isArabic ? reverseWordsKeepWhitespaceByLines(cleanText) : cleanText,
      },
      dispatch
    );
  });

  // 任何语言选择都重置标题与正文，保证字段内容与当前语言一致。
  const handleLanguageChange = () => {
    dispatch(change('EditTemplateForm', 'title', ''));
    dispatch(change('EditTemplateForm', 'text', ''));
  };

  return (
    <form className="EditTemplateForm" onSubmit={submit}>
      <Field
        component={ReduxSelectField}
        name="language"
        maxMenuHeight={100}
        textId="justLanguage"
        options={languages}
        validate={[required()]}
        disabled={submitting}
        onChange={handleLanguageChange}
      />
      <span className="EditTemplateForm__langHint">
        <FM id="formTemplatesSwitchLanguageHint" />
      </span>
      <Field
        component={ReduxTextField}
        name="title"
        textId="formTemplatesTitle"
        validate={[required()]}
        rtlWords={isArabic}
      />
      <Field
        component={TextAreaField}
        name="text"
        textId="justText"
        rows="16"
        cols="150"
        validate={[required()]}
        disabled={submitting}
        rtlWords={isArabic}
      />
      {error && <strong className="formError">{error}</strong>}
      <div className="EditTemplateForm__btns">
        <DefaultButton
          textId="doSave"
          type="submit"
          loading={submitting}
          disabled={invalid || pristine || submitting}
          filled
        />
        <DefaultButton
          textId="justCancel"
          type="button"
          onClick={() => history.push('/backoffice/templates')}
          loading={submitting}
          disabled={submitting}
        />
      </div>
      <style jsx>{staticStyles}</style>
    </form>
  );
};

EditTemplateForm.propTypes = {
  id: PropTypes.string.isRequired,
  submitting: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
  pristine: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  languages: PropTypes.array,
  language: PropTypes.string,
  error: PropTypes.string,
};

EditTemplateForm.defaultProps = {
  error: '',
  languages: [],
  language: '',
};

export default compose(
  reduxForm({
    form: 'EditTemplateForm',
    onSubmitSuccess: (result, dispatch, props) => {
      props.history.push('/backoffice/templates');
    },
  }),
  connect(state => ({
    language: formValueSelector('EditTemplateForm')(state, 'language'),
  }))
)(EditTemplateForm);
